<template>
    <v-container fluid>
        <v-row>
            <v-col>
                <h4 class="text-h4">{{$t("stock_issue_voucher")}}</h4>
                <p class="text-subtitle-1">{{$t("used_to_issue_items_from_inventory")}}</p>
            </v-col>
        </v-row>
        <v-divider></v-divider>
        <v-row class="my-2">
            <v-col cols="12" md="3" v-if="hasPermission(permissions.good_issue.create)">
                <v-btn color="primary" block @click="handleNew">
                    <v-icon left>mdi-plus</v-icon>
                    {{$t("create_stock_issue_voucher")}}
                </v-btn>
            </v-col>
            <v-col>
                <v-menu offset-y :close-on-content-click="false">
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn 
                            block 
                            depressed 
                            v-on="on"
                            v-bind="attrs">
                            <v-icon left>mdi-magnify</v-icon>
                            {{$t("search")}}
                        </v-btn>
                    </template>
                    <v-card>
                        <v-card-title>{{$t("search")}}</v-card-title>
                        <v-card-text>
                            <v-row>
                                <v-col>
                                    <v-text-field
                                        :label="$t('code')"
                                        v-model="searchForm.code"
                                        outlined
                                        dense
                                        hide-details="auto"
                                        clearable
                                        @keypress.enter="onSearch"
                                    >
                                    </v-text-field>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col>
                                    <DatePicker
                                        :label="$t('date_from')"
                                        v-model="searchForm.from"
                                        outlined
                                        dense
                                        hide-details
                                        clearable
                                        />
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col>
                                    <DatePicker
                                        :label="$t('date_to')"
                                        v-model="searchForm.to"
                                        outlined
                                        dense
                                        hide-details
                                        clearable
                                        />
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col>
                                    <v-autocomplete
                                        :label="$t('state')"
                                        v-model="searchForm.state_code"
                                        :loading="statesLoading"
                                        :items="states"
                                        outlined
                                        dense
                                        hide-details
                                        clearable>
                                    </v-autocomplete>
                                </v-col>
                            </v-row>
                        </v-card-text>
                        <v-card-actions>
                            <v-row no-gutters>
                                <v-col>
                                    <v-btn  block color="primary" @click="onSearch">
                                        {{$t("search")}}
                                    </v-btn>
                                </v-col>
                            </v-row>

                        </v-card-actions>
                    </v-card>
                </v-menu>
            </v-col>
            <v-spacer></v-spacer>
            <v-col cols="12" md="3">
                <v-text-field
                    :label="$t('search')"
                    rounded
                    outlined
                    v-model="searchForm.code"
                    dense
                    prepend-inner-icon="mdi-magnify"
                    hide-details
                    @keypress.enter="onSearch"
                    >
                </v-text-field>
            </v-col>
        </v-row>
        <v-row>
            <v-col>
                <v-data-table
                    dense
                    :headers="headers"
                    :items="data"
                    :loading="loading"
                    :server-items-length="length"
                    :options.sync="options"
                    :page.sync="page"
                    :footer-props="{
                        'items-per-page-options': [10, 25, 50]
                    }"
                    disable-sort
                    >
                    <template v-slot:[`item.code`]="{item}">
                        <v-btn icon @click="onRowClick(item)">
                            <v-icon>mdi-magnify</v-icon>
                        </v-btn>
                        {{item.code}}
                    </template>
                    <template v-slot:[`item.state`]="{item}">
                        <PStatus :state="item.state"/>
                    </template>
                </v-data-table>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import GoodIssueService from './../../services/stockJournal/goodsIssueVoucher';
import PStatus from '../../components/common/PStatus.vue';
import DatePicker from '../../components/common/DatePicker.vue';
import permissions from './../../data/permissions';
import authorize from '../../utils/authorize';

export default {
    components: {
        PStatus,
        DatePicker
    },
    data() {
        return {
            data: [],
            loading: false,
            length: 0,
            states: [],
            statesLoading: false,
            options: {},
            searchForm: {
                from: null,
                to: null,
                state: null,
                code: null
            },
            page: 1,
            permissions,
            warehouseId: null,
        }
    },
    computed: {
        headers() {
            return [
                { text: this.$t("code"), value: "code" },
                { text: this.$t("date"), value: "date" },
                { text: this.$t("reference"), value: "reference" },
                { text: this.$t("created_by"), value: "created_by" },
                { text: this.$t("state"), value: "state" }
            ]
        }
    },
    watch: {
        options: {
            deep: true,
            handler() {
                this.fetchDataAsync();
            }
        }  
    },
    methods: {
        fetchDataAsync() {
            this.loading = true;
            GoodIssueService.index({...this.options, ...this.searchForm, warehouse_id: this.warehouseId}).then(response => {
                this.data = response.data.data;
                this.length = response.data.count;
            }).finally(() => {
                this.loading = false;
            })
        },
        fetchStatesAsync() {
            this.statesLoading = true;
            GoodIssueService.states().then(response => {
                this.states = response.data;
            }).finally(() => {
                this.statesLoading = false;
            });
        },
        handleNew() {
            this.$router.push({ name: "GoodsIssueVoucherCreate" })
        },
        onRowClick(row) {
            this.$router.push({
                name: "GoodsIssueVoucherDisplay",
                params: {
                    id: row.id
                }
            })
        },
        onSearch() {
            this.page = 1;
            this.fetchDataAsync();
        },
        hasPermission(code) {
            return authorize(code)
        }
    },
    created() {
        this.warehouseId = this.$route.params.warehouseId;
        this.fetchStatesAsync();
    }
}
</script>

<style>

</style>